// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { Button, Input, Modal } from '@imprivata-cloud/components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Endpoints } from '../../../../api/constants';
import type { ImprDir } from '../../../../api/types';
import {
  type AddDirectory,
  useAddImprIdpDirectory,
  useDirectoriesConfig,
} from '../../../../setup/hooks/useImprivataIdp';

import './IdentityUsersEmpty.less';
import UserModal from '../modals/UserModal';

const TRANSLATION_ROOT = 'portal.settings.identity-users';

export interface IdentityUsersEmptyProps {
  directories?: ImprDir[];
  refetchDirectories?: () => void;
  refetchUsers?: () => Promise<unknown>;
}

const IdentityUsersEmpty = ({
  directories,
  refetchDirectories,
  refetchUsers,
}: IdentityUsersEmptyProps) => {
  const { t } = useTranslation();
  const { mutate: addIdpDirectory, isPending: isPendingAdd } = useAddImprIdpDirectory(
    Endpoints.PORTAL_IMPR_IDP_DIR_SAVE,
  );
  const [identityProviderModalOpen, setIdentityProviderModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { data: directoriesConfig, isPending: isPendingGet } = useDirectoriesConfig(false);

  const handleCancel = () => {
    setInputValue('');
    setIdentityProviderModalOpen(false);
  };

  const handleOk = () => {
    const subdomain = inputValue.trim().toLowerCase().replaceAll('.', '-');
    const domain = `${subdomain}.${directoriesConfig?.imprDomainSuffix}`;

    const payload: AddDirectory = {
      addDirectoryRequest: { directory: { domains: [{ domainName: domain, isPrimary: true }] } },
    };

    addIdpDirectory(payload, {
      onSuccess: () => {
        console.log('Saved directory:', domain);
        setInputValue('');
        setIdentityProviderModalOpen(false);
        if (refetchDirectories) {
          refetchDirectories();
        }
      },
    });
  };

  return (
    <>
      <div className="identity-empty">
        <p>{t('common.no-users')}</p>
        {!directories?.[0] ? (
          <Button
            aria-label="add-identity-dir"
            type={'link'}
            onClick={() => setIdentityProviderModalOpen(true)}
          >
            {t(`${TRANSLATION_ROOT}.add-identity-dir`)}
          </Button>
        ) : (
          <Button
            aria-label="button-add-first-user"
            type={'link'}
            onClick={() => setIsUserModalOpen(true)}
            label={t(`${TRANSLATION_ROOT}.add-user`)}
          />
        )}
      </div>

      <Modal
        aria-label="identity-provider-modal"
        open={identityProviderModalOpen}
        title={t(`${TRANSLATION_ROOT}.modal.title`)}
        width={610}
        onOk={handleOk}
        okText={t('common.save')}
        okButtonProps={{ disabled: inputValue.trim() === '' || isPendingGet || isPendingAdd }}
        onCancel={handleCancel}
        maskClosable={false}
      >
        <div className="identity-modal">
          {t(`${TRANSLATION_ROOT}.modal.domain`)}
          <div className="identity-modal-id">
            <Input
              type="text"
              size="middle"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
            />
            <p>{`.${directoriesConfig?.imprDomainSuffix}`}</p>
          </div>
        </div>
      </Modal>

      {isUserModalOpen && (
        <UserModal
          domain={directories?.[0].domains?.[0].domainName}
          closeModal={() => {
            setIsUserModalOpen(false);
          }}
          refetchUsers={refetchUsers}
        />
      )}
    </>
  );
};

export default IdentityUsersEmpty;
